// stripeService.js
// Handles Stripe payment processing and subscription management

import { loadStripe } from '@stripe/stripe-js';
import { auth } from '../firebase';
import { SUBSCRIPTION_TIERS } from './subscriptionService';

const FUNCTIONS_BASE_URL = 'https://us-central1-beautyfinancetracker.cloudfunctions.net';

// Initialize Stripe (you'll need to add your publishable key to environment variables)
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_your_key_here');

const getIdToken = async () => {
  try {
    if (auth?.currentUser) {
      return await auth.currentUser.getIdToken();
    }
  } catch (err) {
    console.warn('Could not get auth token for Stripe call:', err);
  }
  return null;
};

/**
 * Ensure Stripe customer exists for the user
 */
export const ensureStripeCustomer = async (userId, email, name) => {
  const idToken = await getIdToken();
  const response = await fetch(`${FUNCTIONS_BASE_URL}/ensureStripeCustomer`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    },
    body: JSON.stringify({ userId, email, name, idToken }),
  });

  if (!response.ok) {
    let details = '';
    try {
      const data = await response.json();
      details = data?.error ? ` (${data.error})` : '';
    } catch (_) {
      // ignore parse errors
    }
    throw new Error(`Failed to ensure Stripe customer [${response.status}]${details}`);
  }

  return response.json();
};

/**
 * Create Stripe checkout session for subscription
 */
export const createCheckoutSession = async (userId, tierName, userEmail) => {
  try {
    const tier = SUBSCRIPTION_TIERS[tierName.toUpperCase()];
    if (!tier) {
      throw new Error('Invalid subscription tier');
    }
    if (!tier.stripePriceId) {
      throw new Error('Premium price ID is not configured. Set REACT_APP_STRIPE_PRICE_ID_PREMIUM to your €15 Stripe price ID.');
    }

    const idToken = await getIdToken();

    // Call your backend to create checkout session
    const response = await fetch(`${FUNCTIONS_BASE_URL}/createCheckoutSession`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      body: JSON.stringify({
        userId,
        priceId: tier.stripePriceId,
        userEmail,
        tierName: tier.name,
        idToken,
        successUrl: `${window.location.origin}/subscription/success`,
        cancelUrl: `${window.location.origin}/subscription/cancel`
      }),
    });

    if (!response.ok) {
      let details = '';
      try {
        const data = await response.json();
        details = data?.error ? ` (${data.error})` : '';
      } catch (_) {
        // ignore parse errors
      }
      throw new Error(`Failed to create checkout session [${response.status}]${details}`);
    }

    const { url } = await response.json();
    // Redirect via URL (avoid deprecated redirectToCheckout)
    window.location.href = url;

  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
};

/**
 * Create Stripe customer portal session for subscription management
 */
export const createCustomerPortalSession = async (userId) => {
  try {
    const idToken = await getIdToken();
    const response = await fetch(`${FUNCTIONS_BASE_URL}/createPortalSession`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      body: JSON.stringify({
        userId,
        idToken,
        returnUrl: window.location.origin
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to create portal session');
    }

    const { url } = await response.json();
    
    // Redirect to Stripe Customer Portal
    window.location.href = url;

  } catch (error) {
    console.error('Error creating portal session:', error);
    throw error;
  }
};

/**
 * Get subscription pricing for display
 */
export const getSubscriptionPricing = () => {
  return Object.values(SUBSCRIPTION_TIERS).map(tier => ({
    id: tier.id,
    name: tier.name,
    price: tier.price,
    currency: tier.currency,
    interval: tier.interval,
    features: Object.entries(tier.features).map(([key, value]) => ({
      name: formatFeatureName(key),
      value: formatFeatureValue(key, value),
      included: !!value
    })),
    description: tier.description,
    popular: tier.id === 'premium' // Mark Premium as popular
  }));
};

/**
 * Format feature names for display
 */
const formatFeatureName = (key) => {
  const featureNames = {
    receiptsPerMonth: 'Receipts per month',
    storageGB: 'Storage',
    exportFormats: 'Export formats',
    support: 'Support',
    advancedReporting: 'Advanced reporting',
    multiCurrency: 'Multi-currency',
    apiAccess: 'API access',
    voiceToText: 'Voice to text',
    receiptScanner: 'Receipt scanner',
    customIntegrations: 'Custom integrations'
  };
  
  return featureNames[key] || key;
};

/**
 * Format feature values for display
 */
const formatFeatureValue = (key, value) => {
  switch (key) {
    case 'receiptsPerMonth':
      return value === -1 ? 'Unlimited' : value.toString();
    case 'storageGB':
      return `${value} GB`;
    case 'exportFormats':
      return Array.isArray(value) ? value.join(', ') : value;
    case 'advancedReporting':
    case 'multiCurrency':
    case 'apiAccess':
    case 'voiceToText':
    case 'receiptScanner':
    case 'customIntegrations':
      return value ? 'Included' : 'Not included';
    default:
      return value.toString();
  }
};

/**
 * Calculate savings for annual plans (if implemented)
 */
export const calculateAnnualSavings = (monthlyPrice) => {
  const annualPrice = monthlyPrice * 10; // 2 months free
  const monthlyCost = monthlyPrice * 12;
  const savings = monthlyCost - annualPrice;
  const savingsPercentage = Math.round((savings / monthlyCost) * 100);
  
  return {
    annualPrice,
    savings,
    savingsPercentage
  };
};
